"use client";
// ===================================================
// EnvCompareViewer — a PURE viewer: given a route, a state pair (a vs b) and a
// view mode, it renders the comparison. ALL controls live in the DevEnvBar (one
// menu, up top) — this component intentionally has none. Stages: Local (localhost)
// · Edit (vdev) · Preview (GitHub + VPS, on-demand) · Production (vbrain.io, read-only).
// Local renders live only on localhost; Preview isn't a real backed env yet; Production
// can't embed until its CSP ships — each shows a clear note, never a mysterious blank.
// ===================================================
import { useRef, useState, useEffect } from "react";
import { ENVS, ENV_VOCAB, envUrl, type EnvKey } from "@/lib/environments-queries";

export type CompareMode = "slider" | "toggle" | "side";

const isLocalHost = () =>
  typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)(:\d+)?$/.test(window.location.host);

// One pane: a labelled iframe with an always-present "open in new tab" escape
// hatch. Each state has a reason it might not render here — show a clear note,
// never a mysterious blank: Local needs localhost · Preview needs a Vercel build ·
// Production can't embed until its CSP ships.
function EnvPane({
  env, route, refreshKey, remoteBrokeredLocalUrl,
}: { env: EnvKey; route: string; refreshKey?: number; remoteBrokeredLocalUrl?: string }) {
  const local = isLocalHost();
  const onVdevHost = typeof window !== "undefined" && /^vdev\./.test(window.location.host);
  // Off-localhost, the local pane stays a Note UNLESS the caller explicitly hands
  // us an already-validated brokered URL (step 3 of the local-bridge broker spec —
  // see docs/specs/vcontrol-local-bridge-broker-spec-20260918.md §6). No caller
  // passes this yet, so default behavior is unchanged.
  const brokeredLocal = env === "local" && !local ? remoteBrokeredLocalUrl : undefined;
  const src = env === "local" && local ? `${window.location.origin}${route}` : brokeredLocal ?? envUrl(env, route);

  const Caption = (
    <div className="absolute right-2 top-2 z-10 flex items-center gap-2 rounded-md bg-slate-900/85 px-2 py-0.5 backdrop-blur">
      <span className={`text-[11px] font-semibold rounded border px-1.5 py-0.5 ${ENVS[env].chip}`}>{ENVS[env].label}</span>
      {env !== "preview" && <a href={src} target="_blank" rel="noreferrer" className="text-[11px] text-teal-300 hover:underline">פתח ↗</a>}
    </div>
  );

  const Note = (text: string) => (
    <div className="relative h-full w-full" data-info-id={`env-${env}`}>
      {Caption}
      <div className="flex h-full flex-col items-center justify-center gap-2 bg-slate-900/40 p-6 text-center">
        <p className="max-w-xs text-sm text-slate-400">{text}</p>
      </div>
    </div>
  );

  if (env === "preview") return Note("Preview = GitHub Preview / אימות ב-VPS. Push = GitHub + VPS Preview (לא deploy ל-Vercel). עדיין אין VPS Preview אמיתי מגובה — placeholder.");
  if (env === "local" && !local && !brokeredLocal) return Note("Local = Fast Work / Scratch — חי רק על localhost (working tree מקומי). עלול להיות לא שמור ולא מגובה ב-GitHub. פתח מסך זה ב-localhost כדי לראות.");

  return (
    <div className="relative h-full w-full" data-info-id={`env-${env}`}>
      {Caption}
      {env === "production" && onVdevHost && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-amber-950/70 px-3 py-1 text-center text-[11px] text-amber-200 backdrop-blur">
          Production = קריאה-בלבד (status/link). אם ריק — כותרות production חוסמות מסגור; לחץ “פתח ↗”. Deploy = פעולה ידנית נפרדת.
        </div>
      )}
      {/* key on refreshKey forces a full iframe remount (= reload) after a vdev branch switch —
          otherwise the "Edit" pane keeps showing the pre-switch content until manually reloaded. */}
      <iframe key={`${env}-${refreshKey ?? 0}`} src={src} title={ENVS[env].label} className="h-full w-full border-0 bg-white" loading="lazy" />
    </div>
  );
}

export function EnvCompareViewer({
  route, a, b, mode, refreshKey, remoteBrokeredLocalUrl,
}: { route: string; a: EnvKey; b: EnvKey; mode: CompareMode; refreshKey?: number; remoteBrokeredLocalUrl?: string }) {
  const [toggleShown, setToggleShown] = useState<"a" | "b">("a");
  const [pos, setPos] = useState(50); // slider divider %
  const [isDragging, setIsDragging] = useState(false); // drives the transparent drag-shield
  const boxRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      if (!dragging.current || !boxRef.current) return;
      const r = boxRef.current.getBoundingClientRect();
      setPos(Math.min(98, Math.max(2, ((e.clientX - r.left) / r.width) * 100)));
    };
    const up = () => { dragging.current = false; setIsDragging(false); document.body.style.userSelect = ""; };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, []);

  return (
    <div dir="rtl" className="flex h-full flex-col gap-2">
      <div ref={boxRef} className="relative min-h-0 flex-1 w-full overflow-hidden rounded-xl border border-slate-700 bg-slate-900">
        {mode === "side" && (
          <div className="flex h-full w-full">
            <div className="relative h-full w-1/2 border-l border-slate-700"><EnvPane env={a} route={route} refreshKey={refreshKey} remoteBrokeredLocalUrl={remoteBrokeredLocalUrl} /></div>
            <div className="relative h-full w-1/2"><EnvPane env={b} route={route} refreshKey={refreshKey} remoteBrokeredLocalUrl={remoteBrokeredLocalUrl} /></div>
          </div>
        )}

        {mode === "toggle" && (
          <div className="relative h-full w-full">
            <div className="absolute right-2 top-2 z-20 flex items-center gap-1 rounded-lg bg-slate-900/80 p-0.5 backdrop-blur">
              <button onClick={() => setToggleShown("a")} className={`rounded px-2 py-1 text-[11px] ${toggleShown === "a" ? "bg-teal-500/25 text-teal-100" : "text-slate-400"}`}>{ENVS[a].label}</button>
              <button onClick={() => setToggleShown("b")} className={`rounded px-2 py-1 text-[11px] ${toggleShown === "b" ? "bg-teal-500/25 text-teal-100" : "text-slate-400"}`}>{ENVS[b].label}</button>
            </div>
            <EnvPane env={toggleShown === "a" ? a : b} route={route} refreshKey={refreshKey} remoteBrokeredLocalUrl={remoteBrokeredLocalUrl} />
          </div>
        )}

        {mode === "slider" && (
          <div className="relative h-full w-full">
            <div className="absolute inset-0"><EnvPane env={b} route={route} refreshKey={refreshKey} remoteBrokeredLocalUrl={remoteBrokeredLocalUrl} /></div>
            <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
              <EnvPane env={a} route={route} refreshKey={refreshKey} remoteBrokeredLocalUrl={remoteBrokeredLocalUrl} />
            </div>
            {/* transparent shield ONLY while dragging — stops the iframes from
                swallowing pointer events so the drag stays smooth */}
            {isDragging && <div className="absolute inset-0 z-30 cursor-ew-resize" />}
            <div className="pointer-events-none absolute top-0 bottom-0 z-40 w-0.5 -translate-x-1/2 bg-teal-400" style={{ left: `${pos}%` }}>
              <button
                onPointerDown={(e) => { e.preventDefault(); dragging.current = true; setIsDragging(true); document.body.style.userSelect = "none"; }}
                className="pointer-events-auto absolute top-1/2 left-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize touch-none items-center justify-center rounded-full border border-teal-300 bg-slate-900 text-teal-300 shadow-lg hover:bg-slate-800">
                ↔
              </button>
            </div>
          </div>
        )}
      </div>

      <div dir="rtl" className="shrink-0 space-y-1">
        <p className="text-[11px] text-slate-500">
          Edit (vdev) נטען מיד · Local רק על localhost · Preview עדיין לא מגובה (placeholder) · Production קריאה-בלבד ועשוי להופיע ריק עד עדכון ה-CSP (לחץ “פתח ↗”).
        </p>
        <p className="text-[11px] text-slate-500">
          {ENV_VOCAB.map((v) => `${v.term} = ${v.means}`).join("  ·  ")}
        </p>
      </div>
    </div>
  );
}
